import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Users, User, UserRound } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { demographics } from "@/data/content";
import { SectionHeader } from "@/components/SectionHeader";

const AnimatedNumber = ({ value, duration = 1600, suffix = "%" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20% 0px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {n}
      {suffix}
    </span>
  );
};

const Bar = ({ label, value, color, delay = 0, suffix = "%" }) => (
  <div data-testid="demo-bar">
    <div className="flex items-center justify-between text-sm mb-1.5">
      <span className="font-medium text-slate-700 dark:text-slate-300">{label}</span>
      <span className="font-bold text-slate-950 dark:text-white tabular-nums">
        {value}
        {suffix}
      </span>
    </div>
    <div className="h-2.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${value}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay, ease: "easeOut" }}
        className="h-full rounded-full"
        style={{ backgroundColor: color }}
      />
    </div>
  </div>
);

const Panel = ({ title, children, testid }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-6"
  >
    <h3 className="font-heading font-bold text-slate-950 dark:text-white mb-5">{title}</h3>
    {children}
  </motion.div>
);

export const Demographics = () => {
  const { t, lang } = useLang();
  const { gender, ages, countries, cities } = demographics;

  return (
    <section
      id="demographics"
      data-testid="demographics-section"
      className="py-16 sm:py-24 bg-slate-50 dark:bg-slate-900/50 border-y border-slate-200 dark:border-slate-800"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          title={t.demographics.title}
          subtitle={t.demographics.subtitle}
        />

        <div className="grid lg:grid-cols-2 gap-4 mt-12">
          <Panel title={t.demographics.genderTitle} testid="demo-gender">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center rounded-lg border border-blue-100 dark:border-blue-900/60 bg-blue-50/60 dark:bg-blue-950/30 p-5">
                <User className="h-7 w-7 mx-auto text-blue-600 dark:text-blue-400" />
                <div className="mt-3 font-heading text-4xl font-black text-blue-600 dark:text-blue-400">
                  <AnimatedNumber value={gender.male} />
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  {t.demographics.male}
                </div>
              </div>
              <div className="text-center rounded-lg border border-pink-100 dark:border-pink-900/60 bg-pink-50/60 dark:bg-pink-950/30 p-5">
                <UserRound className="h-7 w-7 mx-auto text-pink-500 dark:text-pink-400" />
                <div className="mt-3 font-heading text-4xl font-black text-pink-500 dark:text-pink-400">
                  <AnimatedNumber value={gender.female} />
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  {t.demographics.female}
                </div>
              </div>
            </div>
          </Panel>

          <Panel title={t.demographics.ageTitle} testid="demo-ages">
            <div className="space-y-4">
              {ages.map((a, i) => (
                <Bar
                  key={a.range}
                  label={a.range}
                  value={a.value}
                  color="#0EA5E9"
                  delay={i * 0.1}
                />
              ))}
            </div>
          </Panel>

          <Panel title={t.demographics.countriesTitle} testid="demo-countries">
            <div className="space-y-4">
              {countries.map((c, i) => (
                <Bar
                  key={c.en}
                  label={`${c.flag}  ${c[lang]}`}
                  value={c.value}
                  color="#D4AF37"
                  delay={i * 0.1}
                />
              ))}
            </div>
          </Panel>

          <Panel title={t.demographics.citiesTitle} testid="demo-cities">
            <div className="space-y-4">
              {cities.map((c, i) => (
                <Bar
                  key={c.en}
                  label={c[lang]}
                  value={c.value}
                  color="#059669"
                  delay={i * 0.1}
                />
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </section>
  );
};
